import type { ParityTrendDailyPoint } from '~/types/services/priceParrot';

export type ParityTrendMetric = 'pct_overpriced' | 'overpriced_searches';

/** Inclusive YYYY-MM-DD bounds; empty strings yield []. */
export function filterParityTrendDailyByDateRange(
  dailyTrend: Array<ParityTrendDailyPoint>,
  dateFrom: string,
  dateTo: string,
): Array<ParityTrendDailyPoint> {
  if (!dateFrom || !dateTo) {
    return [];
  }
  return dailyTrend.filter((p) => p.report_date >= dateFrom && p.report_date <= dateTo);
}

export function valueForMetric(point: ParityTrendDailyPoint, metric: ParityTrendMetric): number | null {
  if (metric === 'pct_overpriced') {
    return point.pct_overpriced != null ? point.pct_overpriced * 100 : null;
  }
  return point.overpriced_searches;
}

/** Per-date region values only (no `report_date` mixed into the same object). */
type RegionValueMap = Partial<Record<string, number | null>>;

/** One Recharts row: ISO date plus one numeric column per region key. */
export type ParityTrendChartRow = {
  report_date: string;
} & Record<string, string | number | null>;

/**
 * Pivots long-format daily_trend into wide rows for Recharts (one column per region).
 */
export function buildParityTrendWideRows(
  dailyTrend: Array<ParityTrendDailyPoint>,
  metric: ParityTrendMetric,
): {
  rows: Array<ParityTrendChartRow>;
  regions: Array<string>;
} {
  const regions = [...new Set(dailyTrend.map((p) => p.region))].sort();
  const byDate = new Map<string, RegionValueMap>();

  for (const p of dailyTrend) {
    const v = valueForMetric(p, metric);
    const existing: RegionValueMap = byDate.get(p.report_date) ?? {};
    existing[p.region] = v;
    byDate.set(p.report_date, existing);
  }

  const sortedDates = [...byDate.keys()].sort();
  const rows: Array<ParityTrendChartRow> = sortedDates.map((date) => {
    const base = byDate.get(date);
    if (!base) {
      return { report_date: date };
    }
    const row: ParityTrendChartRow = { report_date: date };
    for (const r of regions) {
      row[r] = base[r] ?? null;
    }
    return row;
  });

  return { rows, regions };
}

/**
 * Distinct stroke colors for **parity trend** lines (API region strings, not OTAs).
 * Intentionally separate from `CHART_LINE_COLORS` in `constants.ts`, which maps **supplier**
 * names for the price-comparison chart — similar palette for familiarity, but not shared so
 * region order/index mapping stays local to this chart.
 */
export const REGION_TREND_LINE_COLORS = [
  '#2563eb',
  '#f59e0b',
  '#c026d3',
  '#10b981',
  '#dc2626',
  '#0891b2',
  '#7c3aed',
  '#ea580c',
] as const;

export function regionTrendColor(region: string, regions: Array<string>): string {
  const idx = regions.indexOf(region);
  return REGION_TREND_LINE_COLORS[idx % REGION_TREND_LINE_COLORS.length] ?? '#64748b';
}

export function chipStyleForRegion(region: string, regions: Array<string>) {
  const line = regionTrendColor(region, regions);
  return {
    bg: `${line}22`,
    color: line,
    border: line,
  };
}
